import { controlledSignal } from "@tanstack/charts/interaction/signal"
import { decorative } from "@tanstack/charts/mark/decorative"
import { scaleBand } from "@tanstack/charts/scales/band"
import { scaleLinear } from "@tanstack/charts/scales/linear"
import { keyedSelection } from "@tanstack/charts/selection"
import { bandX, rect, ruleX, text } from "@tanstack/charts/universal"
import { annotationMarksOf } from "./annotations.ts"
import { dayLabel } from "@beacon/core/days"
import { dayTicksOf } from "./day-ticks.ts"
import { formatOf, type DailyChartInput } from "./input.ts"
import { themeOf } from "./theme.ts"

/*
 * What every daily chart shares, whatever its marks: the day axis, the bands behind the marks for
 * days not known and the day picked, the annotations over them, picking a day, and the chart's ink.
 */

/** One series' value on one day: a bar's segment or a line's point. */
export type DailyValue<K extends string> = {
  day: string
  series: K
  label: string
  value: number | null
}

/**
 * Room above the plot for the annotations' labels, which stand on top of their lines: what the web
 * app's buttons over those labels are sized and placed by.
 */
export const annotationRoom = 20

/** Every series' value on every day, null where the day's history isn't known. */
export function valuesOf<K extends string>({ series, days }: DailyChartInput<K>) {
  return days.flatMap(({ day, values }) =>
    series.map(({ key, label }) => ({ day, series: key, label, value: values?.[key] ?? null })),
  )
}

/**
 * The value axis, settled here rather than inferred from the marks, so the annotations' labels can
 * stand at its top: the input's own ticks, the last its top; whole counts from zero to past `most`,
 * the largest value a mark reaches, where the values are counts; or else a nice scale from zero.
 */
export function valueAxis<K extends string>(most: number, input: DailyChartInput<K>) {
  const { valueTicks, formatValue } = input
  const given = valueTicks ?? (formatValue ? undefined : { values: countTicksOf(most) })
  const scale = given
    ? scaleLinear().domain([0, given.values.at(-1)!])
    : scaleLinear()
        .domain([0, most || 1])
        .nice()
  const ticks = given
    ? { values: given.values, format: valueTicks?.format ?? formatOf(input) }
    : { format: formatOf(input) }
  return {
    top: scale.domain()[1],
    options: { scale, grid: true, axis: { ...axisWordsOf(input, input.unit), ticks } },
  }
}

/** An axis's title and tick labels, in the input's `text` where it sets them. */
function axisWordsOf<K extends string>({ text }: DailyChartInput<K>, title: string) {
  if (!text) return { label: title }
  const { size, opacity } = text
  return {
    label: { text: title, fontSize: size, opacity },
    tickLabels: { fontSize: size, opacity },
  }
}

/**
 * A count's ticks: about four whole steps of 1, 2 or 5 of a power of ten from zero, the last at or
 * past `most`, so a count is never written as a fraction, and [0, 1] when there is nothing.
 */
export function countTicksOf(most: number) {
  const rough = Math.max(most, 1) / 4
  const power = 10 ** Math.max(0, Math.floor(Math.log10(rough)))
  const step = [1, 2, 5, 10].map((multiple) => multiple * power).find((s) => s >= rough) ?? power
  const top = Math.max(step, Math.ceil(most / step) * step)
  return Array.from({ length: top / step + 1 }, (_, index) => index * step)
}

/**
 * The share of a day's width left between two days' bars, half of it at either end of the plot: so
 * each day owns exactly its step of the plot, its bar in the middle 70%.
 */
export const dayGap = 0.3

/**
 * Each day of the range in order on the x axis, titled as UTC and labelled "3 Sept" at one even
 * calendar step that has room in the plot's width.
 */
export function dayAxis<K extends string>(input: DailyChartInput<K>) {
  const { days, axisWidth = 640 } = input
  const domain = days.map(({ day }) => day)
  const { label, tickLabels } = axisWordsOf(input, "Day (UTC)")
  return {
    scale: scaleBand<string>()
      .domain(domain)
      .paddingInner(dayGap)
      .paddingOuter(dayGap / 2),
    axis: {
      label,
      ticks: { values: dayTicksOf(domain, axisWidth), format: dayLabel },
      // A label wider than its room still gives way, rather than overlap its neighbour.
      tickLabels: { ...tickLabels, thin: { minGap: 8 } },
    },
  }
}

/** The picked day, while it is one of the chart's: a day picked outside the range shows nothing. */
export function pickedOf<K extends string>({ days, picked }: DailyChartInput<K>) {
  return picked && days.some(({ day }) => day === picked) ? picked : undefined
}

/**
 * The x scale a span of days is drawn on: day `i` covers [i, i + 1], edge to edge across its step
 * of the plot, which the day axis's padding leaves each day exactly.
 */
const daySpan = "day-span" as const

/** The day-span scale, laid over the day axis and drawn nowhere. */
export function daySpanScale<K extends string>({ days }: DailyChartInput<K>) {
  return {
    [daySpan]: {
      scale: scaleLinear().domain([0, Math.max(days.length, 1)]),
      channel: "x" as const,
      axis: false as const,
    },
  }
}

/** Each run of days in a row whose history isn't known, as the day-span scale places it. */
function unknownRunsOf<K extends string>({ days }: DailyChartInput<K>) {
  const runs: { from: number; to: number }[] = []
  days.forEach(({ values }, index) => {
    if (values !== null) return
    const last = runs.at(-1)
    if (last && last.to === index) last.to = index + 1
    else runs.push({ from: index, to: index + 1 })
  })
  return runs
}

/**
 * Behind the marks: each day's stop for focus, which tints the day in focus and holds a stop on a
 * day not known too; each run of days whose history isn't known as one muted span, saying so where
 * it is wide enough; and the picked day's band.
 */
export function backdropsOf<K extends string>(input: DailyChartInput<K>, top: number) {
  const { days, paints, axisWidth = 640 } = input
  const picked = pickedOf(input)
  const runs = unknownRunsOf(input)
  // Wide enough to say so: "Not known" at 11px and room either side.
  const told = runs.filter(({ from, to }) => ((to - from) / days.length) * axisWidth >= 88)
  return [
    // The day in focus, faintly, whatever its day holds: a hover, a tap or the keyboard's place.
    rect(days, {
      id: "days",
      x: "day",
      y1: () => 0,
      y2: () => top,
      inset: 0,
      fill: paints("foreground"),
      fillOpacity: 0,
      states: [{ when: { focus: "x" }, style: { fillOpacity: 0.05 } }],
    }),
    decorative(
      rect(runs, {
        id: "unknown",
        x1: "from",
        x2: "to",
        y1: () => 0,
        y2: () => top,
        xScale: daySpan,
        inset: 0,
        fill: paints("muted"),
      }),
    ),
    decorative(
      text(told, {
        id: "unknown-labels",
        x: ({ from, to }) => (from + to) / 2,
        y: () => top / 2,
        xScale: daySpan,
        text: () => "Not known",
        fill: paints("muted-foreground"),
        fontSize: input.text?.size ?? 11,
        ...(input.text && { fillOpacity: input.text.opacity }),
      }),
    ),
    decorative(
      // Wider than the day's own band by 4px a side, so a narrow day's bar never hides it.
      bandX(picked ? [picked] : [], {
        id: "picked",
        x: (day) => day,
        // The ink a step past the focus tint, which reads on a light card and a dark one alike.
        fill: paints("foreground"),
        fillOpacity: 0.08,
        inset: -4,
        radius: 3,
      }),
    ),
  ]
}

/**
 * Over the marks: each annotated day's dashed line, and each mark's label standing above the plot
 * at `top`, a mark standing for every annotation too close to stand apart.
 */
export function annotationsOf<K extends string>(input: DailyChartInput<K>, top: number) {
  const { annotations = [], paints } = input
  const paint = paints(input.annotationPaint ?? "muted-foreground")
  const days = [...new Set(annotations.map(({ day }) => day))].map((day) => ({ day }))
  return [
    ruleX(days, {
      id: "annotation-lines",
      x: "day",
      stroke: paint,
      strokeOpacity: 0.6,
      strokeDasharray: "3 3",
    }),
    decorative(
      text(annotationMarksOf(input), {
        id: "annotation-labels",
        x: "day",
        y: () => top,
        text: "label",
        dy: -annotationRoom / 2,
        fill: paint,
        fontSize: 11,
        fontWeight: 500,
      }),
    ),
  ]
}

/** What a daily chart's spec shares: its colours, its ink, the room its annotations take. */
export function dailySpecOf<K extends string>(input: DailyChartInput<K>) {
  const { series, paints, annotations = [] } = input
  return {
    color: {
      domain: series.map(({ key }) => key),
      range: series.map(({ paint }) => paints(paint)),
    },
    ...(annotations.length > 0 && { margin: { top: annotationRoom } }),
    theme: themeOf(paints),
  }
}

/**
 * Picking a day: a click, a tap, Enter or Space proposes the day in focus, or none on a click off
 * every mark. A day whose history isn't known is never proposed: there is nothing on it to pick.
 * The caller decides, and rebuilds the chart with its pick.
 */
export function dayPickingOf<K extends string>(input: DailyChartInput<K>) {
  const { days, picked = null, onPick } = input
  const unknown = new Set(days.flatMap(({ day, values }) => (values === null ? [day] : [])))
  return {
    // Focus steps a day at a time, so the tooltip and the keyboard tell the whole day.
    focus: "group-x" as const,
    // The day in focus is tinted by its stop, not ringed at a bar's top as a line's point is.
    focusRing: false,
    selection: keyedSelection<{ day: string }, string>({
      selected: controlledSignal(picked, (day) => {
        if (day === null || !unknown.has(day)) onPick?.(day)
      }),
      key: ({ day }) => day,
    }),
  }
}
